import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Save, Send, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

const POLICY_SLUGS = [
  { slug: "refund-policy", label: "Refund Policy" },
  { slug: "terms", label: "Terms & Conditions" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "contact", label: "Contact" },
];

export function AdminSettings() {
  const [settings, setSettings] = useState({
    admin_email: "", link_validity_days: 7, max_downloads: 5,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [policies, setPolicies] = useState({});
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [policyContent, setPolicyContent] = useState("");
  const [savingPolicy, setSavingPolicy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    POLICY_SLUGS.forEach(({ slug }) => {
      api.get(`/policies/${slug}`).then(({ data }) => {
        setPolicies((prev) => ({ ...prev, [slug]: data.content }));
      }).catch(() => {});
    });
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  }

  async function handleTestEmail() {
    setTesting(true);
    try {
      await api.post("/admin/test-email");
      toast.success("Test email sent! Check your inbox.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test email");
    } finally { setTesting(false); }
  }

  async function handleSavePolicy() {
    if (!editingPolicy) return;
    setSavingPolicy(true);
    try {
      await api.put(`/admin/policies/${editingPolicy}`, { content: policyContent });
      setPolicies((prev) => ({ ...prev, [editingPolicy]: policyContent }));
      toast.success("Policy updated");
      setEditingPolicy(null);
    } catch { toast.error("Failed to save policy"); }
    finally { setSavingPolicy(false); }
  }

  return (
    <div className="mt-4 space-y-6">
      {/* Delivery Settings */}
      <div className="bg-white rounded-2xl p-5 space-y-4">
        <h3 className="font-heading font-bold text-base">Delivery Settings</h3>
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Sale alert email</label>
          <Input value={settings.admin_email}
            onChange={(e) => setSettings({ ...settings, admin_email: e.target.value })}
            className="rounded-xl" placeholder="your@email.com" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Link valid (days)</label>
            <Input type="number" value={settings.link_validity_days}
              onChange={(e) => setSettings({ ...settings, link_validity_days: Number(e.target.value) })}
              className="rounded-xl" min={1} max={30} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Max downloads</label>
            <Input type="number" value={settings.max_downloads}
              onChange={(e) => setSettings({ ...settings, max_downloads: Number(e.target.value) })}
              className="rounded-xl" min={1} max={50} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving}
            className="bg-coral hover:bg-coral-hover text-white rounded-full flex-1 font-bold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save Settings
          </Button>
          <Button onClick={handleTestEmail} disabled={testing} variant="outline" className="rounded-full">
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Test Email
          </Button>
        </div>
      </div>

      {/* Policy Pages */}
      <div className="bg-white rounded-2xl p-5 space-y-3">
        <h3 className="font-heading font-bold text-base">Policy Pages</h3>
        <p className="text-xs text-slate-400">These pages are linked in the footer. Edit the text for your business.</p>
        {POLICY_SLUGS.map(({ slug, label }) => (
          <div key={slug} className="flex items-center justify-between py-2 border-b border-border last:border-0">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">{label}</span>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs rounded-full"
              onClick={() => { setEditingPolicy(slug); setPolicyContent(policies[slug] || ""); }}>
              Edit
            </Button>
          </div>
        ))}
      </div>

      {/* Policy Edit Dialog (inline) */}
      {editingPolicy && (
        <div className="fixed inset-0 z-50 bg-cream overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
            <h2 className="font-heading font-bold text-lg">
              Edit {POLICY_SLUGS.find((p) => p.slug === editingPolicy)?.label}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => setEditingPolicy(null)}>Cancel</Button>
          </div>
          <div className="max-w-2xl mx-auto p-4 space-y-4">
            <Textarea value={policyContent} onChange={(e) => setPolicyContent(e.target.value)}
              rows={15} className="rounded-xl font-body text-sm resize-none" />
            <Button onClick={handleSavePolicy} disabled={savingPolicy}
              className="w-full bg-coral hover:bg-coral-hover text-white rounded-full font-bold">
              {savingPolicy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Policy
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
